import { useReducedMotion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

// Cursor stir, kept soft. Radius as a share of the canvas's short side
// (~170px on a 1440px hero).
const STIR_RADIUS = 0.14
// Smoke carried along by the cursor: seconds of cursor motion it follows,
// capped at this share of the radius so a flick doesn't tear the cloud.
const STIR_DRAG = 0.08
const STIR_DRAG_MAX = 0.45
// Wisps: how far the noise tears the smoke (share of the radius), and how
// much it thins it out, toward white.
const STIR_TURBULENCE = 0.16
const STIR_THINNING = 0.14
// How fast the stir point catches up with the cursor (1/s): a lazy follow
// reads as smoke, a 1:1 follow looks mechanical.
const FOLLOW_RATE = 5
// Smoothing of the cursor velocity that drives the drag (1/s).
const DRIFT_RATE = 6
// The stir builds over ~1s while the cursor moves, and settles over ~1s once
// it has rested this long (ms).
const IDLE_MS = 450
const STIR_IN_RATE = 2.5
const STIR_OUT_RATE = 2.5

/**
 * Drifting blue "cloud" behind the landing hero: the 21st.dev "Waves" recipe
 * (fbm domain warp + sine bands + grain), recoloured CountAct and reduced to
 * the knobs we use. The palette runs bottom → top white, lavender, brand blue,
 * pale lavender, white: the blue peaks behind the video frame and the shader
 * fades back to white on its own at both ends, so neither the headline nor the
 * section below sits on saturated colour.
 *
 * A moving mouse stirs the cloud: around the cursor the smoke is dragged along
 * the motion, torn into wisps and thinned out, then settles back once the
 * cursor rests. Touch is ignored (a swipe there is a scroll, not a hover).
 */
const FRAGMENT = `
precision highp float;
uniform vec2 u_res;
uniform float u_time;
uniform vec3 u_colors[5];
// Cursor in canvas pixels (xy) and stir strength 0–1 (z); its smoothed
// velocity, canvas pixels per second.
uniform vec3 u_mouse;
uniform vec2 u_drift;

float hash(vec2 p) {
  p = fract(p * vec2(234.34, 435.345));
  p += dot(p, p + 34.23);
  return fract(p.x * p.y);
}
// Separate hash for the grain: the one above leaves a faint grid on flat areas.
float grainHash(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
             mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * noise(p);
    p = p * 2.03 + vec2(17.0, 9.2);
    a *= 0.5;
  }
  return v;
}
// WebGL1 forbids dynamic uniform indexing, hence the unrolled ramp.
vec3 palette(float x) {
  float f = clamp(x, 0.0, 1.0) * 4.0;
  vec3 c = u_colors[0];
  c = mix(c, u_colors[1], smoothstep(0.0, 1.0, clamp(f, 0.0, 1.0)));
  c = mix(c, u_colors[2], smoothstep(0.0, 1.0, clamp(f - 1.0, 0.0, 1.0)));
  c = mix(c, u_colors[3], smoothstep(0.0, 1.0, clamp(f - 2.0, 0.0, 1.0)));
  c = mix(c, u_colors[4], smoothstep(0.0, 1.0, clamp(f - 3.0, 0.0, 1.0)));
  return c;
}
void main() {
  // Cursor stir: within the radius, sample behind the motion (the smoke is
  // carried along) and through a churning noise field (it breaks into wisps).
  vec2 frag = gl_FragCoord.xy;
  vec2 d = frag - u_mouse.xy;
  float r = ${STIR_RADIUS.toFixed(3)} * min(u_res.x, u_res.y);
  float stir = exp(-dot(d, d) / (r * r)) * u_mouse.z;
  float thin = 0.0;
  if (stir > 0.002) {
    vec2 q = frag / r * 1.3;
    vec2 drag = u_drift * ${STIR_DRAG.toFixed(3)};
    drag *= min(1.0, ${STIR_DRAG_MAX.toFixed(3)} * r / max(length(drag), 0.001));
    vec2 wisp = vec2(fbm(q + u_time * 0.5), fbm(q + vec2(3.1, 7.7) - u_time * 0.5)) - 0.5;
    frag -= (drag + wisp * r * ${STIR_TURBULENCE.toFixed(3)}) * stir;
    thin = stir * smoothstep(0.4, 0.7, fbm(q * 1.7 - u_time * 0.3));
  }
  vec2 uv = frag / u_res;
  vec2 p = (frag - 0.5 * u_res) / min(u_res.x, u_res.y);
  p *= 1.72;
  p = mat2(cos(2.91), -sin(2.91), sin(2.91), cos(2.91)) * p + vec2(0.05, -0.11);
  p += 0.39 * (vec2(fbm(p * 1.568 + 6.0), fbm(p * 1.568 + vec2(5.2, 1.3))) - 0.5);
  float y = uv.y
    + sin(uv.x * 7.05 + u_time * 0.8) * 0.08
    + (fbm(p * 2.0 + u_time * 0.1) - 0.5) * 0.27;
  // < 1 pushes the ramp up the canvas: the blue band sits ~70% down.
  vec3 col = palette(pow(clamp(y, 0.0, 1.0), 0.545));
  // Stirred smoke thins out in patches, like it is dissipating.
  col = mix(col, vec3(1.0), thin * ${STIR_THINNING.toFixed(3)});
  col += (grainHash(gl_FragCoord.xy) - 0.5) * 0.035;
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
}
`

const VERTEX = `
attribute vec2 a_position;
void main() { gl_Position = vec4(a_position, 0.0, 1.0); }
`

// Bottom → top. Brand blue (#4B58F4) is a lighter step of --primary-vivid so the
// band reads as a glow, not a block.
const COLORS = ['#FFFFFF', '#D3DBFF', '#4B58F4', '#E6EBFF', '#FFFFFF']
// Negative: the waves drift the other way, matching the 21st.dev preset.
const TIME_SCALE = -0.65
// Frame drawn under reduced motion, picked for a balanced cloud.
const STILL_TIME = 4
// Soft shader: half the device pixels is visually identical and 4x cheaper.
const RESOLUTION = 0.5
const MAX_PIXELS = 2_000_000

export function HeroShader({ className }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const reduceMotion = useReducedMotion()
  const [drawing, setDrawing] = useState(false)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const gl = canvas.getContext('webgl', {
      antialias: false,
      premultipliedAlpha: false,
    })
    if (!gl) return

    const compile = (type: number, source: string) => {
      const shader = gl.createShader(type)
      if (!shader) return null
      gl.shaderSource(shader, source)
      gl.compileShader(shader)
      if (gl.getShaderParameter(shader, gl.COMPILE_STATUS)) return shader
      if (import.meta.env.DEV) console.error(gl.getShaderInfoLog(shader))
      gl.deleteShader(shader)
      return null
    }
    const vertex = compile(gl.VERTEX_SHADER, VERTEX)
    const fragment = vertex && compile(gl.FRAGMENT_SHADER, FRAGMENT)
    if (!vertex || !fragment) return
    const program = gl.createProgram()
    gl.attachShader(program, vertex)
    gl.attachShader(program, fragment)
    gl.linkProgram(program)
    gl.deleteShader(vertex)
    gl.deleteShader(fragment)
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      gl.deleteProgram(program)
      return
    }
    gl.useProgram(program)

    // One oversized triangle covers the viewport; the fragment shader does the rest.
    const buffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 3, -1, -1, 3]),
      gl.STATIC_DRAW,
    )
    const position = gl.getAttribLocation(program, 'a_position')
    gl.enableVertexAttribArray(position)
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)
    gl.uniform3fv(
      gl.getUniformLocation(program, 'u_colors'),
      // '#RRGGBB' → [r, g, b] in 0–1, flattened for the vec3 array.
      new Float32Array(
        COLORS.flatMap((hex) =>
          [1, 3, 5].map((i) => Number.parseInt(hex.slice(i, i + 2), 16) / 255),
        ),
      ),
    )
    const uRes = gl.getUniformLocation(program, 'u_res')
    const uTime = gl.getUniformLocation(program, 'u_time')
    const uMouse = gl.getUniformLocation(program, 'u_mouse')
    const uDrift = gl.getUniformLocation(program, 'u_drift')

    let frame = 0
    let last = 0
    let elapsed = STILL_TIME
    let inView = true
    let lost = false
    // Cursor in canvas pixels, y up like gl_FragCoord: where it is (target),
    // where the stir trails behind it (mouse), its velocity (drift), and the
    // stir strength 0–1.
    let targetX = 0
    let targetY = 0
    let mouseX = 0
    let mouseY = 0
    let driftX = 0
    let driftY = 0
    let stir = 0
    let over = false
    let lastMove = Number.NEGATIVE_INFINITY

    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      const ratio = Math.min(2, window.devicePixelRatio || 1) * RESOLUTION
      let width = Math.max(1, Math.round(rect.width * ratio))
      let height = Math.max(1, Math.round(rect.height * ratio))
      const scale = Math.min(1, Math.sqrt(MAX_PIXELS / (width * height)))
      width = Math.max(1, Math.round(width * scale))
      height = Math.max(1, Math.round(height * scale))
      if (canvas.width === width && canvas.height === height) return
      canvas.width = width
      canvas.height = height
      gl.viewport(0, 0, width, height)
    }
    const draw = () => {
      if (lost) return
      gl.uniform2f(uRes, canvas.width, canvas.height)
      gl.uniform1f(uTime, elapsed * TIME_SCALE)
      gl.uniform3f(uMouse, mouseX, mouseY, stir)
      gl.uniform2f(uDrift, driftX, driftY)
      gl.drawArrays(gl.TRIANGLES, 0, 3)
    }
    const loop = (now: number) => {
      // Clamp the step so a backgrounded tab doesn't jump the cloud on return.
      const step = last ? Math.min(0.1, (now - last) / 1000) : 0
      elapsed += step
      last = now
      const prevX = mouseX
      const prevY = mouseY
      const follow = 1 - Math.exp(-step * FOLLOW_RATE)
      mouseX += (targetX - mouseX) * follow
      mouseY += (targetY - mouseY) * follow
      if (step) {
        const smooth = 1 - Math.exp(-step * DRIFT_RATE)
        driftX += ((mouseX - prevX) / step - driftX) * smooth
        driftY += ((mouseY - prevY) / step - driftY) * smooth
      }
      const goal = over && now - lastMove < IDLE_MS ? 1 : 0
      const rate = goal > stir ? STIR_IN_RATE : STIR_OUT_RATE
      stir += (goal - stir) * (1 - Math.exp(-step * rate))
      draw()
      frame = requestAnimationFrame(loop)
    }
    const stop = () => {
      cancelAnimationFrame(frame)
      last = 0
    }
    const start = () => {
      stop()
      if (!reduceMotion && inView && !document.hidden)
        frame = requestAnimationFrame(loop)
    }

    resize()
    draw()
    setDrawing(true)
    start()

    const resizeObserver = new ResizeObserver(() => {
      resize()
      draw()
    })
    resizeObserver.observe(canvas)
    const intersection = new IntersectionObserver(([entry]) => {
      inView = entry?.isIntersecting ?? true
      start()
    })
    intersection.observe(canvas)
    document.addEventListener('visibilitychange', start)
    // Read on the window: the canvas sits under the hero copy and video, so it
    // never receives pointer events itself. Mouse and pen only, and never under
    // reduced motion (the loop is off, so the stir could not settle back).
    const onPointerMove = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return
      const rect = canvas.getBoundingClientRect()
      const x = (event.clientX - rect.left) / rect.width
      const y = (event.clientY - rect.top) / rect.height
      over = x >= 0 && x <= 1 && y >= 0 && y <= 1
      if (!over) return
      targetX = x * canvas.width
      targetY = (1 - y) * canvas.height
      // Stir at rest: start at the cursor instead of sweeping in from its
      // last spot.
      if (stir < 0.01) {
        mouseX = targetX
        mouseY = targetY
        driftX = 0
        driftY = 0
      }
      lastMove = performance.now()
    }
    if (!reduceMotion)
      window.addEventListener('pointermove', onPointerMove, { passive: true })
    // Lost context (GPU reset, too many contexts): fade the canvas out rather
    // than leave a frozen or black frame.
    const onLost = (event: Event) => {
      event.preventDefault()
      lost = true
      stop()
      setDrawing(false)
    }
    canvas.addEventListener('webglcontextlost', onLost)

    return () => {
      stop()
      resizeObserver.disconnect()
      intersection.disconnect()
      document.removeEventListener('visibilitychange', start)
      window.removeEventListener('pointermove', onPointerMove)
      canvas.removeEventListener('webglcontextlost', onLost)
      // No `loseContext()`: StrictMode re-runs this effect on the same canvas,
      // and a lost context can't be reacquired from it.
      gl.deleteBuffer(buffer)
      gl.deleteProgram(program)
    }
  }, [reduceMotion])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className={cn(
        // Entrance once the first frame is drawn: the cloud comes into focus
        // (blur + slight zoom out + fade). Nothing is painted before it, so
        // without JS or WebGL the hero simply stays white.
        'block transition-[opacity,filter,scale] duration-[1400ms] ease-out motion-reduce:transition-none',
        drawing
          ? 'scale-100 opacity-100 blur-none'
          : 'scale-110 opacity-0 blur-2xl',
        className,
      )}
    />
  )
}
